import {
  collection,
  getDocs,
  query,
  where,
  writeBatch,
  updateDoc,
} from 'firebase/firestore'
import { db } from '@/firebase/init'
import { resetOrderNumbering } from '@/globals/typescript/utils/orderNumbering'

const BATCH_SIZE = 500

async function deleteCollection(
  collectionPath: string[],
  filterField: string,
  accountID: string
): Promise<void> {
  const q = query(
    collection(db, ...(collectionPath as [string, ...string[]])),
    where(filterField, '==', accountID)
  )

  const snapshot = await getDocs(q)
  if (snapshot.empty) return

  const docs = snapshot.docs
  for (let i = 0; i < docs.length; i += BATCH_SIZE) {
    const batch = writeBatch(db)
    docs.slice(i, i + BATCH_SIZE).forEach((d) => batch.delete(d.ref))
    await batch.commit()
  }
}

/**
 * The three customer price collections, keyed by nothing in particular — the
 * shape of the pricing document is identical across all three customer types
 * (see `customerPricingAdapters.ts`, which relies on the same fact).
 */
const CUSTOMER_PRICE_COLLECTIONS = [
  'CustomerAccountPrices',
  'InsuranceCustomerAccountPrices',
  'CustomerPrivatePrices',
] as const

/**
 * Wipes every customer-level pricing override for a lab, not just the segment
 * assignment.
 *
 * This used to reset `pricingSegmentID` alone and leave `analyses`, `tats` and
 * `addons` untouched — which meant every "clear all" left overrides behind that
 * referenced the TAT, analysis and add-on documents the steps above had just
 * deleted. Those orphans are how pre-`defaultAction` TAT entries were still
 * sitting in dev data long after the shape changed.
 *
 * Iterates each price collection directly. The old version derived the
 * `CustomerAccountPrices` doc from `CustomerAccounts` and called `updateDoc` on
 * it, which throws outright when a customer has no pricing document yet.
 */
async function resetCustomerPricing(accountID: string): Promise<void> {
  for (const collectionName of CUSTOMER_PRICE_COLLECTIONS) {
    const snapshot = await getDocs(
      query(
        collection(db, 'Apps', '-lab-management-', collectionName),
        where('labAccountID', '==', accountID)
      )
    )
    if (snapshot.empty) continue

    const docs = snapshot.docs
    for (let i = 0; i < docs.length; i += BATCH_SIZE) {
      const batch = writeBatch(db)
      docs.slice(i, i + BATCH_SIZE).forEach((priceDoc) => {
        batch.update(priceDoc.ref, {
          pricingSegmentID: null,
          analyses: [],
          tats: [],
          addons: [],
          deals: [],
          'timestamps.updatedAt': Date.now(),
        })
      })
      await batch.commit()
    }
  }
}

export async function runClear(accountID: string): Promise<void> {
  // 1. Orders
  await deleteCollection(
    ['Apps', '-lab-customer-', 'Orders'],
    'labAccountID',
    accountID
  )

  // 2. Samples
  await deleteCollection(
    ['Apps', '-lab-customer-', 'Samples'],
    'labAccountID',
    accountID
  )

  // 3. AnalysisResults
  await deleteCollection(
    ['Apps', '-lab-customer-', 'AnalysisResults'],
    'labAccountID',
    accountID
  )

  // 4. Invoices
  await deleteCollection(
    ['Apps', '-lab-customer-', 'Invoices'],
    'labAccountID',
    accountID
  )

  // 5. Analyses
  await deleteCollection(
    ['Apps', '-lab-management-', 'Analyses'],
    'accountID',
    accountID
  )

  // 6. AnalysisTests
  await deleteCollection(
    ['Apps', '-lab-management-', 'AnalysisTests'],
    'accountID',
    accountID
  )

  // 7. SampleAreas
  await deleteCollection(
    ['Apps', '-lab-management-', 'SampleAreas'],
    'accountID',
    accountID
  )

  // 8. PricingSegments (before AddonServices — pricing segments reference addon IDs)
  await deleteCollection(
    ['Apps', '-lab-management-', 'PricingSegments'],
    'labAccountID',
    accountID
  )

  // 9. AddonServices
  await deleteCollection(
    ['Apps', '-lab-management-', 'AddonServices'],
    'labAccountID',
    accountID
  )

  // 10. TextLists
  await deleteCollection(
    ['Apps', '-lab-management-', 'TextLists'],
    'labAccountID',
    accountID
  )

  // 11. TATs
  await deleteCollection(
    ['Apps', '-lab-management-', 'TATs'],
    'accountID',
    accountID
  )

  // 12. ReportDescriptionItems (before ReportDescriptions — items reference descriptions)
  await deleteCollection(
    ['Apps', '-lab-management-', 'ReportDescriptionItems'],
    'labAccountID',
    accountID
  )

  // 13. ReportDescriptions
  await deleteCollection(
    ['Apps', '-lab-management-', 'ReportDescriptions'],
    'labAccountID',
    accountID
  )

  // 14. ReportTemplates
  await deleteCollection(
    ['Apps', '-lab-management-', 'ReportTemplates'],
    'labAccountID',
    accountID
  )

  // 15. LabEquipmentMaintenance (before LabEquipment — references equipment IDs)
  await deleteCollection(
    ['Apps', '-lab-management-', 'LabEquipmentMaintenance'],
    'labAccountID',
    accountID
  )

  // 16. LabEquipment
  await deleteCollection(
    ['Apps', '-lab-management-', 'LabEquipment'],
    'labAccountID',
    accountID
  )

  // 17. ClosedDays — nothing references a closed day, so ordering does not
  // matter here; it is cleared so a wiped lab does not keep a calendar that
  // silently shifts every expected delivery date on the next seeded order.
  await deleteCollection(
    ['Apps', '-lab-management-', 'ClosedDays'],
    'labAccountID',
    accountID
  )

  // 18–20. Reset customer pricing for this lab, across all three customer types.
  await resetCustomerPricing(accountID)

  // 21. Clear walkInTatIDs on all LabDropPoints for this lab
  const dropPointsQuery = query(
    collection(db, 'Apps', '-lab-management-', 'LabDropPoints'),
    where('labAccountID', '==', accountID)
  )
  const dropPointsSnapshot = await getDocs(dropPointsQuery)
  for (const dropPointDoc of dropPointsSnapshot.docs) {
    await updateDoc(dropPointDoc.ref, { walkInTatIDs: [] })
  }

  // 22. Order numbering — the two LabAccount counters.
  // Shared with `orderStore.deleteAllOrdersAndSamples` ("Slet ordrer"), which used to skip
  // this entirely, so the two clear paths disagreed about what clearing orders meant. Keep
  // it in the helper rather than inlining it here, or they will drift again.
  await resetOrderNumbering(accountID)
}
